import { useState, useEffect } from 'react';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import { 
  AlertTriangle, TrendingUp, Users, Building2, GraduationCap, 
  Activity, Layers, Crown, ChevronRight, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icons for different limit types
const LIMIT_ICONS = {
  max_usuarios_total: Users,
  max_instructores: Users,
  max_alumnos: GraduationCap,
  max_establecimientos: Building2,
  max_cursos: Layers,
  max_simulaciones_mes: Activity
};

// Display names for limits
const LIMIT_NAMES = {
  max_usuarios_total: 'Usuarios Totales',
  max_instructores: 'Instructores',
  max_alumnos: 'Alumnos',
  max_establecimientos: 'Establecimientos',
  max_cursos: 'Cursos',
  max_simulaciones_mes: 'Simulaciones/Mes'
};

// Limit Status Badge
export const LimitStatusBadge = ({ limit }) => {
  if (limit.unlimited) {
    return <Badge className="bg-green-100 text-green-700">Ilimitado</Badge>;
  }
  
  if (!limit.can_create) {
    return <Badge className="bg-red-100 text-red-700">Límite alcanzado</Badge>;
  }
  
  if (limit.warning) {
    return <Badge className="bg-yellow-100 text-yellow-700">Cerca del límite</Badge>;
  }
  
  return <Badge className="bg-green-100 text-green-700">Disponible</Badge>;
};


// Single Limit Display
export const LimitDisplay = ({ code, limit }) => {
  const Icon = LIMIT_ICONS[code] || Activity;
  const name = LIMIT_NAMES[code] || code;
  
  const percentage = limit.unlimited ? 0 : limit.percentage;
  const progressColor = limit.warning ? 'bg-yellow-500' : (limit.can_create ? 'bg-blue-500' : 'bg-red-500');
  
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium">{name}</span>
        </div>
        <span className="text-sm text-gray-500">
          {limit.unlimited ? '∞' : `${limit.current}/${limit.max}`}
        </span>
      </div>
      {!limit.unlimited && (
        <Progress value={percentage} className="h-2" indicatorClassName={progressColor} />
      )}
    </div>
  );
};


// Plan Limits Summary Popover
export const PlanLimitsSummary = ({ onUpgrade }) => {
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    fetchLimits();
  }, []);
  
  const fetchLimits = async () => {
    try {
      const response = await fetch(`${API_URL}/api/limits/summary`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setLimits(data);
      }
    } catch (error) {
      console.error('Error fetching limits:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Determine overall status
  const getOverallStatus = () => {
    if (!limits?.limits) return 'ok';
    
    const limitValues = Object.values(limits.limits);
    const hasBlocked = limitValues.some(l => !l.unlimited && !l.can_create);
    const hasWarning = limitValues.some(l => !l.unlimited && l.warning);
    
    if (hasBlocked) return 'blocked';
    if (hasWarning) return 'warning';
    return 'ok';
  };
  
  const status = getOverallStatus();
  
  if (loading) {
    return (
      <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100">
        <Loader2 className="w-4 h-4 animate-spin" />
      </div>
    );
  }
  
  if (!limits?.has_subscription) {
    return null; // Don't show if no subscription
  }
  
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button 
          className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${
            status === 'blocked' 
              ? 'bg-red-100 text-red-700 hover:bg-red-200' 
              : status === 'warning'
                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
          }`}
          data-testid="plan-limits-trigger"
        >
          {status === 'blocked' ? (
            <AlertTriangle className="w-4 h-4" />
          ) : status === 'warning' ? (
            <TrendingUp className="w-4 h-4" />
          ) : (
            <Crown className="w-4 h-4" />
          )}
          <span className="text-sm font-medium">{limits.plan_name}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-semibold">Uso del Plan</h4>
            <Badge variant="outline">{limits.plan_name}</Badge>
          </div>
          
          <div className="space-y-3">
            {Object.entries(limits.limits || {}).map(([code, limit]) => (
              <LimitDisplay key={code} code={code} limit={limit} />
            ))}
          </div>
          
          {status !== 'ok' && onUpgrade && (
            <Button 
              className="w-full" 
              variant={status === 'blocked' ? 'destructive' : 'outline'}
              onClick={onUpgrade}
            >
              <Crown className="w-4 h-4 mr-2" />
              Mejorar Plan
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};


// Limit Exceeded Dialog
export const LimitExceededDialog = ({ open, onOpenChange, limitCode, limitInfo, onUpgrade }) => {
  const Icon = LIMIT_ICONS[limitCode] || AlertTriangle;
  const name = LIMIT_NAMES[limitCode] || 'Recurso';
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Límite de Plan Alcanzado
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-4 pt-2">
              <div className="flex items-center gap-3 p-4 bg-red-50 rounded-lg">
                <Icon className="w-8 h-8 text-red-500" />
                <div>
                  <p className="font-medium text-red-800">{name}</p>
                  <p className="text-sm text-red-600">
                    {limitInfo?.current || 0} de {limitInfo?.limit || 0} utilizados
                  </p>
                </div>
              </div>
              
              <p className="text-gray-600">
                Ha alcanzado el límite máximo de {name.toLowerCase()} en su plan actual. 
                Para continuar creando recursos, considere actualizar su plan o contacte 
                a soporte para opciones adicionales.
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
          {onUpgrade && (
            <Button onClick={onUpgrade} className="bg-blue-600 hover:bg-blue-700">
              <Crown className="w-4 h-4 mr-2" />
              Ver Planes
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};


// Hook to check limits before creating resources
export const useLimitCheck = () => {
  const [checkingLimit, setCheckingLimit] = useState(false);
  const [limitDialog, setLimitDialog] = useState({ open: false, code: null, info: null });
  
  const checkLimit = async (limitCode) => {
    setCheckingLimit(true);
    try {
      const response = await fetch(`${API_URL}/api/limits/check/${limitCode}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (!data.can_create && !data.is_unlimited) {
          setLimitDialog({
            open: true,
            code: limitCode,
            info: {
              current: data.current_value,
              limit: data.max_value
            }
          });
          return false;
        }
        
        return true;
      }
      
      return true; // Allow if check fails
    } catch (error) {
      console.error('Error checking limit:', error);
      return true; // Allow if check fails
    } finally {
      setCheckingLimit(false);
    }
  };
  
  const closeLimitDialog = () => {
    setLimitDialog({ open: false, code: null, info: null });
  };
  
  return {
    checkLimit,
    checkingLimit,
    limitDialog,
    closeLimitDialog,
    LimitExceededDialog: () => (
      <LimitExceededDialog
        open={limitDialog.open}
        onOpenChange={closeLimitDialog}
        limitCode={limitDialog.code}
        limitInfo={limitDialog.info}
        onUpgrade={() => {
          closeLimitDialog();
          // Navigate to plans page or show upgrade modal
          window.location.href = '/#planes';
        }}
      />
    )
  };
};

export default PlanLimitsSummary;
